// Pure helper for deriving both AI auto filter sections from the search response.

import type { AiAutoFilterItem, GeographicalResolution } from './types';

export function buildAiAutoFilterSections(
  geographicalResolution: GeographicalResolution | null | undefined,
  detectedEmailAddresses: string[] | undefined,
  detectedOzoneIds?: string[] | undefined
): {
  appliedFilters: AiAutoFilterItem[];
  unappliedDetections: AiAutoFilterItem[];
} {
  const geo = geographicalResolution ?? {};
  const appliedFilters: AiAutoFilterItem[] = [];
  const unappliedDetections: AiAutoFilterItem[] = [];

  const validatedAddresses = uniqueNonEmpty((geo.validated_addresses ?? []).map((a) => a.formatted_address));
  const validatedStreets = uniqueNonEmpty([geo.validated_street?.formatted_street]);
  const parcelIds = uniqueNonEmpty((geo.parcels ?? []).map((p) => p.parcel_id));
  const ratingValuations = uniqueNonEmpty((geo.rating_valuations ?? []).map((v) => v.valuation_reference_number));
  const legalDescriptions = uniqueNonEmpty((geo.parcels ?? []).map((p) => p.appellation));
  const emails = uniqueNonEmpty(detectedEmailAddresses ?? []);
  const ozoneIds = uniqueNonEmpty(detectedOzoneIds ?? []);

  if (validatedAddresses.length > 0) appliedFilters.push(autoFilterItem('applied-address', 'Address', validatedAddresses, true));
  if (validatedStreets.length > 0) appliedFilters.push(autoFilterItem('applied-street', 'Street', validatedStreets, true));
  if (parcelIds.length > 0) appliedFilters.push(autoFilterItem('applied-parcel_id', 'Parcel ID', parcelIds, true));
  if (ratingValuations.length > 0) appliedFilters.push(autoFilterItem('applied-valuation_reference_number', 'Rating valuation', ratingValuations, true));
  if (legalDescriptions.length > 0) appliedFilters.push(autoFilterItem('applied-legal_description', 'Legal description', legalDescriptions, true));
  if (emails.length > 0) appliedFilters.push(autoFilterItem('applied-email', 'Email', emails, true));
  if (ozoneIds.length > 0) appliedFilters.push(autoFilterItem('applied-ozone_id', 'Ozone ID', ozoneIds, true));

  const parsedAddresses = uniqueNonEmpty((geo.parsed_addresses ?? []).map((a) => a.formatted_address));
  const unvalidatedParsed = parsedAddresses.filter((parsedAddress) => {
    return !validatedAddresses.includes(parsedAddress);
  });
  if (unvalidatedParsed.length > 0) {
    unappliedDetections.push(autoFilterItem('unapplied-address', 'Address', unvalidatedParsed, false));
  }

  if (validatedStreets.length === 0) {
    const parsedStreets = uniqueNonEmpty([geo.parsed_street?.formatted_street]);
    if (parsedStreets.length > 0) {
      unappliedDetections.push(autoFilterItem('unapplied-street', 'Street', parsedStreets, false));
    }
  }

  return { appliedFilters, unappliedDetections };
}

function autoFilterItem(key: string, label: string, values: string[], applied: boolean): AiAutoFilterItem {
  return {
    key,
    label,
    values,
    description: applied
      ? `${label} applied as a search filter.`
      : `${label} detected in your search query but not applied as a filter.`,
  };
}

function uniqueNonEmpty(values: Array<string | undefined | null>): string[] {
  return [...new Set(values.map((v) => v?.trim()).filter((v): v is string => Boolean(v)))];
}
